import {
  createFileRoute,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { Loading } from "@/Loading";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Button } from "@/Button";
import { Dialog } from "@/Dialog";
// [임시] 위탁계약 주석 처리로 미사용
// import ContractModal from "@/common/ContractModal";
import { motion } from "framer-motion";
import { PetDataType } from "~/types/common";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import {
  useBusinessManualReserveTicketList,
  useBusinessManualReservePetList,
  useBusinessManualReserveReservableDate,
  useBusinessManualReserveReservableTime,
  useBusinessManualReserveCalendar,
  useCreateBusinessManualReserve,
  checkBusinessReservationCapacity,
} from "~/services/business/reserve";
import { getErrorMessage } from "%/errorCode";
import useHideNav from "^/common/useHideNav";

/** 발급 상세에서 전달하는 state 타입 */
export interface ReservationWithIssuedTicketState {
  userSeq?: number;
  purchaseTicketSeq: number;
  cdProductType?: string;
  ticketName: string;
  remainQty: number;
  qty: number;
  userName: string;
}

export const Route = createFileRoute(
  "/business/layout/reservation-with-issued-ticket"
)({
  component: ReservationWithIssuedTicket,
});

function ReservationWithIssuedTicket() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as unknown as
    | ReservationWithIssuedTicketState
    | undefined;

  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  // 유치원 정원 초과 경고(비즈: 확인/취소) — 초과 날짜
  const [capacityWarnDates, setCapacityWarnDates] = useState<string[] | null>(null);
  const capacityConfirmedRef = useRef(false);
  const [isDateListExpanded, setIsDateListExpanded] = useState(true);
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [selectedMinute, setSelectedMinute] = useState<string | null>(null);
  // 데이케어 시간권: 이용시간(시간 수)
  const [selectedUseHours, setSelectedUseHours] = useState<number | null>(null);
  // const [showContractDialog, setShowContractDialog] = useState(false);
  // const [showContractModal, setShowContractModal] = useState(false);
  const [showReservationCompleteDialog, setShowReservationCompleteDialog] =
    useState(false);
  // 수기예약: 활성 사업장 + 미계약(hasContract=false)이면 예약 후 계약 안내
  const [showContractNotice, setShowContractNotice] = useState(false);
  const [duplicateReserveMessage, setDuplicateReserveMessage] = useState<
    string | null
  >(null);

  // companySeq 는 서버가 토큰(JWT)에서 직접 꺼내 쓴다. 프론트가 알 필요가 없고,
  // 원장 전용 API(company-info)로 받아오면 선생님 계정에서 0 이 되어 화면이 막힌다.
  const { data: ticketList = [], isLoading: isTicketListLoading } =
    useBusinessManualReserveTicketList({ userSeq: state?.userSeq ?? 0 });

  const fixedTicket = useMemo(() => {
    if (!state?.purchaseTicketSeq || !ticketList.length) return null;
    return (
      ticketList.find(
        (t) => t.purchaseTicketSeq === state.purchaseTicketSeq
      ) ?? null
    );
  }, [ticketList, state?.purchaseTicketSeq]);

  const {
    data: petList = [],
    isLoading: isPetLoading,
    // refetch: refetchPetList, // [임시] 위탁계약 ContractModal 복구 시 사용
  } = useBusinessManualReservePetList({ userSeq: state?.userSeq ?? 0 });

  // 이용권에 귀속된 petSeq로 반려동물 자동 고정 (펫 단위 이용권)
  const selectedPet: PetDataType | null = useMemo(() => {
    if (!fixedTicket?.petSeq || petList.length === 0) return null;
    return petList.find((p) => p.petSeq === fixedTicket.petSeq) ?? null;
  }, [fixedTicket?.petSeq, petList]);

  // 예약 플로우 동안 하단 nav 숨김 — 조건(selectedPet)만 되면 예약하기 버튼 상시 노출
  useHideNav();

  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const formatYearMonth = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    return `${y}${m}`;
  };

  const formatYearMonthDay = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}${m}${d}`;
  };

  const [currentMonth, setCurrentMonth] = useState(() =>
    formatYearMonth(new Date())
  );

  const cdProductType =
    state?.cdProductType ?? fixedTicket?.cdProductType ?? "";

  const { data: reservableDateData } =
    useBusinessManualReserveReservableDate(
      currentMonth,
      !!(selectedPet && state?.purchaseTicketSeq),
      cdProductType || undefined,
      selectedPet?.petSeq || undefined
    );

  // 캘린더 화면과 동일한 패턴으로 /business/manual/reserve/calendar 호출
  // currentMonth(yyyyMM) → yyyyMMdd 의 1일자로 변환하여 조회
  const { data: manualReserveCalendarData } = useBusinessManualReserveCalendar(
    selectedPet?.petSeq ?? 0,
    currentMonth ? `${currentMonth}01` : "",
    !!selectedPet
  );

  const isHotel = cdProductType === "PRODUCT_TYPE_HOTEL";
  // 데이케어 시간권: 단일 날짜 + 시작시간(정시) + 이용시간(시간 수)으로 예약
  const isDaycare = cdProductType === "PRODUCT_TYPE_DAYCARE";
  // 유치원(등원 수업): 날짜 다중 선택. 정원 제한 대상(데이케어와 함께)
  const isKindergarten = cdProductType === "PRODUCT_TYPE_KINDERGARTEN";

  const firstSelectedDate = selectedDates[0] ?? null;

  const { data: reservableTimeData, isFetching: isReservableTimeFetching } =
    useBusinessManualReserveReservableTime(
      firstSelectedDate ? formatYearMonthDay(firstSelectedDate) : "",
      (isHotel || isDaycare) && !!firstSelectedDate
    );

  // 데이케어 이용시간 Select 범위: 최소 = 이용권 최소 이용 수량(없으면 1), 최대 = 잔여 시간
  const daycareMinUseHours = fixedTicket?.minUseQty ?? 1;
  const daycareMaxUseHours = fixedTicket?.remainCount ?? 0;
  const daycareUseHoursOptions = useMemo(() => {
    if (!isDaycare || daycareMaxUseHours < daycareMinUseHours) {
      return [] as number[];
    }
    return Array.from(
      { length: daycareMaxUseHours - daycareMinUseHours + 1 },
      (_, i) => daycareMinUseHours + i
    );
  }, [isDaycare, daycareMinUseHours, daycareMaxUseHours]);

  const hourOptions = useMemo(() => {
    if (!reservableTimeData?.startTime || !reservableTimeData?.endTime) {
      return [] as string[];
    }
    const start = Number.parseInt(
      reservableTimeData.startTime.split(":")[0] ?? "0",
      10
    );
    const end = Number.parseInt(
      reservableTimeData.endTime.split(":")[0] ?? "0",
      10
    );
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];
    const list: string[] = [];
    for (let h = start; h <= end; h++) {
      list.push(String(h).padStart(2, "0"));
    }
    return list;
  }, [reservableTimeData?.startTime, reservableTimeData?.endTime]);

  const minuteOptions = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) =>
        String(i * 10).padStart(2, "0")
      ),
    []
  );

  // 호텔: 시작일이 바뀔 때마다 선택한 시/분 초기화
  const firstDateKey = firstSelectedDate
    ? formatYearMonthDay(firstSelectedDate)
    : "";
  useEffect(() => {
    if (!isHotel && !isDaycare) return;
    setSelectedHour(null);
    setSelectedMinute(null);
    setSelectedUseHours(null);
  }, [isHotel, isDaycare, firstDateKey]);

  const { mutate: createBusinessManualReserve, isPending: isReserving } =
    useCreateBusinessManualReserve();

  const getReservationCompleteMessage = (productType: string) => {
    switch (productType) {
      case "PRODUCT_TYPE_KINDERGARTEN":
        return "수업예약이 접수되었습니다.";
      case "PRODUCT_TYPE_DAYCARE":
        return "데이케어 서비스가 접수되었습니다.";
      case "PRODUCT_TYPE_HOTEL":
        return "호텔 서비스가 접수되었습니다.";
      case "PRODUCT_TYPE_GROOMING":
        return "미용 서비스가 접수되었습니다.";
      default:
        return "예약이 접수되었습니다.";
    }
  };

  const handleCalendarMonthChange = useCallback((isNext: boolean) => {
    setCurrentMonth((prev) => {
      const match = /(\d{4})(\d{2})/.exec(prev);
      const yearStr = match?.[1];
      const monthStr = match?.[2];
      if (!yearStr || !monthStr) {
        const today = new Date();
        const nextDate = new Date(
          today.getFullYear(),
          today.getMonth() + (isNext ? 1 : -1),
          1
        );
        return formatYearMonth(nextDate);
      }
      let y = Number.parseInt(yearStr, 10);
      let m = Number.parseInt(monthStr, 10) - 1;
      if (isNext) {
        m += 1;
        if (m > 11) {
          m = 0;
          y += 1;
        }
      } else {
        m -= 1;
        if (m < 0) {
          m = 11;
          y -= 1;
        }
      }
      return formatYearMonth(new Date(y, m, 1));
    });
  }, []);

  const disabledDates = useMemo(() => {
    if (!reservableDateData?.calendar || !currentMonth) return [];
    const calendar = reservableDateData.calendar;
    const reservedDates = reservableDateData.reservedDates ?? [];
    const reservedDateSet = new Set(reservedDates);
    // 임시 휴무일: 호텔은 calendar에 반영되지 않으므로 직접 클릭(체크인) 차단.
    // 자동 채움(체크인 이후 연속일)은 disabledDates 영향을 받지 않아 기간 포함은 허용됨.
    const holidaySet = new Set(reservableDateData.holidays ?? []);
    const disabled: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const match = /(\d{4})(\d{2})/.exec(currentMonth);
    const yearStr = match?.[1];
    const monthStr = match?.[2];
    if (!yearStr || !monthStr) return [];
    const year = Number.parseInt(yearStr, 10);
    const month = Number.parseInt(monthStr, 10) - 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      const dateString = formatYearMonthDay(date);
      let isReservable = false;
      if (Array.isArray(calendar)) {
        isReservable = calendar.includes(dateString);
      } else if (typeof calendar === "object" && calendar !== null) {
        const v = (calendar as Record<string, unknown>)[dateString];
        isReservable = v !== undefined && v !== false && v !== null;
      }
      const isDuplicateReserved = reservedDateSet.has(dateString);
      const isHoliday = holidaySet.has(dateString);
      if ((!isReservable || isDuplicateReserved || isHoliday) && date >= today) {
        disabled.push(date);
      }
    }
    return disabled;
  }, [reservableDateData, currentMonth]);

  const maxSelectableDates = isDaycare
    ? 1 // 데이케어는 하루 단위 시간 예약이므로 단일 날짜만 선택
    : fixedTicket != null
      ? fixedTicket.remainCount
      : (state?.remainQty ?? 0);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const hotelTotalDays = fixedTicket?.totalCount ?? state?.qty ?? 0;

  const handleDateChange = (days: Date[] | Date | null) => {
    if (days === null) {
      setSelectedDates([]);
      return;
    }
    const arr = Array.isArray(days) ? days : [days];

    // 데이케어: 마지막으로 클릭한 단일 날짜만 선택
    if (isDaycare) {
      const newDate = arr.find(
        (d) => !selectedDates.some((sd) => isSameDay(sd, d))
      );
      if (!newDate) {
        setSelectedDates([]);
        return;
      }
      setSelectedDates([newDate]);
      return;
    }

    // 호텔: 새로 클릭한 날짜를 체크인으로, (박수+1)일 연속 자동 선택
    // 예) 1박 → 체크인+체크아웃 2일 표시, 2박 → 3일 표시
    if (isHotel) {
      const newDate = arr.find(
        (d) => !selectedDates.some((sd) => isSameDay(sd, d))
      );
      if (!newDate) {
        // 기존 선택일 토글 → 전체 해제
        setSelectedDates([]);
        return;
      }
      const nights = hotelTotalDays > 0 ? hotelTotalDays : 1;
      const filled: Date[] = [];
      for (let i = 0; i <= nights; i++) {
        const d = new Date(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate() + i
        );
        filled.push(d);
      }
      setSelectedDates(filled);
      return;
    }

    if (maxSelectableDates > 0 && arr.length > maxSelectableDates) {
      setSelectedDates(arr.slice(0, maxSelectableDates));
      return;
    }
    setSelectedDates(arr);
  };

  const handleBackClick = useCallback(() => {
    // 브라우저 back 우선 — 명시 push는 발급상세↔예약 히스토리 핑퐁을 만든다
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    navigate({
      to: "/business/layout/user-subscription-issue-detail",
      search: {
        purchaseTicketSeq: state?.purchaseTicketSeq ?? 0,
      },
      replace: true,
    });
  }, [navigate, state?.purchaseTicketSeq]);

  const handleReservation = async () => {
    if (isReserving) return; // 연타로 인한 중복 예약 방지
    if (!selectedPet) {
      alert("반려동물을 선택해주세요.");
      return;
    }
    if (selectedDates.length === 0) {
      alert("날짜를 선택해주세요.");
      return;
    }
    if (!fixedTicket) {
      alert("이용권 정보가 없습니다.");
      return;
    }
    if (isHotel && (!selectedHour || !selectedMinute)) {
      alert("시작 시간을 선택해주세요.");
      return;
    }
    if (isDaycare && !selectedHour) {
      alert("입소 시간을 선택해주세요.");
      return;
    }
    if (isDaycare && !selectedUseHours) {
      alert("이용시간을 선택해주세요.");
      return;
    }

    // 유치원 정원 체크 — 유치원(등원)·데이케어. 초과 시 확인/취소 팝업(확인=초과여도 진행).
    if ((isKindergarten || isDaycare) && !capacityConfirmedRef.current) {
      const dateStrs = selectedDates.map((d) => formatDate(d));
      try {
        const cap = await checkBusinessReservationCapacity(dateStrs);
        if (cap.limitEnabled && cap.fullDates.length > 0) {
          setCapacityWarnDates(cap.fullDates);
          return;
        }
      } catch (e) {
        console.warn("정원 체크 실패 — 예약 진행", e);
      }
    }
    capacityConfirmedRef.current = false;

    const pets = petList.map((pet: PetDataType) => ({
      petSeq: pet.petSeq,
      petName: pet.petName,
      select: pet.petSeq === selectedPet.petSeq,
    }));

    const sortedDates = [...selectedDates].sort(
      (a, b) => a.getTime() - b.getTime()
    );
    const startDate = sortedDates[0];

    // 호텔: 체크아웃 = 체크인 + (totalCount * 24h). totalCount일은 정확히 N일 뒤 같은 시각.
    const nights =
      hotelTotalDays > 0 ? hotelTotalDays : selectedDates.length || 1;
    const checkoutDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate() + nights
    );
    const startTimeStr = `${selectedHour}:${selectedMinute}:00`;

    // 호텔: 숙박 기간(체크인 다음날~체크아웃)에 임시 휴무일이 끼어 있으면 확인 후 진행
    if (isHotel) {
      const holidaySet = new Set(reservableDateData?.holidays ?? []);
      let hasHolidayInStay = false;
      for (let i = 1; i <= nights; i++) {
        const d = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate() + i
        );
        if (holidaySet.has(formatYearMonthDay(d))) {
          hasHolidayInStay = true;
          break;
        }
      }
      if (
        hasHolidayInStay &&
        !window.confirm("예약일정에 휴무일이 있습니다. 계속 진행하시겠습니까?")
      ) {
        return;
      }
    }

    // 데이케어: 시작시간(정시) + 이용시간 → 종료시간 계산
    const daycareStartTime = `${selectedHour}:00:00`;
    const daycareEndTime = `${String(
      Number.parseInt(selectedHour ?? "0", 10) + (selectedUseHours ?? 0)
    ).padStart(2, "0")}:00:00`;

    let reserveForm: {
      productSeq?: number;
      dates?: string[];
      dtReservStart?: string;
      dtReservEnd?: string;
      startTime?: string;
      endTime?: string;
      dayOfWeeks?: Array<{
        cdDayOfWeek: string;
        dayOfWeekName: string;
        select: boolean;
      }>;
      paymentCoin?: number;
      additionals: Array<{
        productSeq?: number;
        additionalCompanySeq?: number;
        paymentCoin: number;
        additionalName: string;
        active: boolean;
        select: boolean;
      }>;
      pets: Array<{ petSeq: number; petName: string; select: boolean }>;
      memo: string | null;
    };
    if (isHotel) {
      reserveForm = {
        dtReservStart: formatDate(startDate),
        dtReservEnd: formatDate(checkoutDate),
        startTime: startTimeStr,
        endTime: startTimeStr,
        additionals: [],
        pets,
        memo: null,
      };
    } else if (isDaycare) {
      reserveForm = {
        dtReservStart: formatDate(startDate),
        startTime: daycareStartTime,
        endTime: daycareEndTime,
        dayOfWeeks: [],
        additionals: [],
        pets,
        memo: null,
      };
    } else {
      reserveForm = {
        dates: selectedDates.map((d) => formatDate(d)),
        dayOfWeeks: [],
        additionals: [],
        pets,
        memo: null,
      };
    }

    // 차감 시간: 호텔=박수, 데이케어=이용시간, 그 외=선택 일수
    const paymentTicket = isHotel
      ? nights
      : isDaycare
        ? (selectedUseHours ?? 0)
        : selectedDates.length;

    const requestData = {
      reservationSeq: null,
      cdProductType,
      gradeConfirm: true,
      paymentCoinTotal: null,
      tickets: [
        {
          purchaseTicketSeq: fixedTicket.purchaseTicketSeq,
          productTicketSeq: fixedTicket.productTicketSeq,
          paymentTicket,
        },
      ],
      reserveForm,
    };
    createBusinessManualReserve(requestData, {
      onSuccess: (response) => {
        // 같은 매장 같은 그룹 중복 → 차단
        if (response?.statusCode === 4009) {
          setDuplicateReserveMessage(
            response?.message ?? "선택한 날짜에 같은 유형의 예약이 이미 등록되어 있습니다."
          );
          return;
        }
        // 활성 사업장 + 미계약(hasContract=false) → 예약은 접수됐고, 계약서 발송 안내(선택)
        if (response?.data?.hasContract === false) {
          setShowContractNotice(true);
        } else {
          setShowReservationCompleteDialog(true);
        }
      },
      onError: (error: unknown) => {
        alert(getErrorMessage(error, "예약 생성에 실패했습니다. 다시 시도해주세요."));
      },
    });
  };

  if (!state?.purchaseTicketSeq || !state?.remainQty) {
    return (
      <>
        <LayoutHeader
          title="예약하기"
          hasBackBtn
          hasFootPrint={false}
          onBackClick={() =>
            navigate({
              to: "/business/layout/user-subscription-issue-history",
              replace: true, // 잘못된 진입 화면이 히스토리에 남지 않도록
            })
          }
        />
        <div className="flex flex-col justify-center items-center py-20 text-[#666666]">
          <p className="mb-2">이용권 정보가 없습니다.</p>
          <p className="text-sm">
            발급 상세에서 예약하기 버튼으로 이동해 주세요.
          </p>
        </div>
      </>
    );
  }

  if (state?.userSeq && isTicketListLoading) {
    return (
      <>
        <LayoutHeader
          title="예약하기"
          hasBackBtn
          hasFootPrint={false}
          onBackClick={handleBackClick}
        />
        <div className="flex justify-center items-center py-20">
          <Loading visible={true} />
        </div>
      </>
    );
  }

  const displayTicketName =
    fixedTicket?.name ?? state?.ticketName ?? "";
  const displayRemain = fixedTicket?.remainCount ?? state?.remainQty ?? 0;
  const displayTotal = fixedTicket?.totalCount ?? state?.qty ?? 0;

  return (
    <>
      <LayoutHeader
        title="예약하기"
        hasBackBtn
        hasFootPrint={false}
        onBackClick={handleBackClick}
      />
      <div className="flex flex-col flex-auto bg-[#F5F5F5] pb-24 overflow-x-hidden">
        <div className="px-4 pt-4 pb-6 max-w-full">
          {/* 고정 이용권 정보 (전달받은 purchaseTicketSeq로 API 리스트에서 선택) */}
          <div className="mb-4">
            <h2 className="mb-2 text-base font-semibold text-gray-800">
              이용권
            </h2>
            <div className="p-4 bg-white rounded-xl border border-orange-400 shadow-sm">
              <div className="flex justify-between items-start w-full min-w-0">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-800 break-words">
                    {displayTicketName}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    발급 대상: {state.userName}
                  </p>
                  {!isHotel && (
                    <p className="mt-1 text-sm font-medium">
                      <span className="text-gray-700">
                        {displayTotal - displayRemain}회 사용
                      </span>
                      <span
                        className={
                          displayRemain === 0
                            ? "ml-1 text-gray-400"
                            : "ml-1 text-orange-500"
                        }
                      >
                        ({displayRemain}회 남음)
                      </span>
                    </p>
                  )}
                </div>
                <div className="flex flex-shrink-0 justify-center items-center ml-2 w-5 h-5 bg-orange-400 rounded-full">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* 반려동물 (이용권에 귀속된 펫 고정 표시) */}
          <div className="mt-4">
            <h3 className="mb-2 text-base font-semibold text-gray-800">
              반려동물
            </h3>
            {isPetLoading ? (
              <div className="p-4 text-center text-gray-500 bg-white rounded-xl border border-gray-200 shadow-sm">
                반려동물 정보를 불러오는 중...
              </div>
            ) : selectedPet ? (
              <div className="p-4 bg-white rounded-xl border border-orange-400 shadow-sm">
                <div className="flex justify-between items-start">
                  <div className="flex flex-1 gap-3 items-center">
                    {selectedPet.fileUrl ? (
                      <img
                        src={selectedPet.fileUrl}
                        alt={selectedPet.petName}
                        className="object-cover w-12 h-12 rounded-full"
                      />
                    ) : (
                      <div className="flex justify-center items-center w-12 h-12 bg-gray-200 rounded-full">
                        <svg
                          className="w-6 h-6 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="mb-1 text-base font-semibold text-gray-800">
                        {selectedPet.petName}
                      </h4>
                      <div className="flex gap-2 items-center text-sm text-gray-600">
                        {selectedPet.petBreedName ? (
                          <span>품종: {selectedPet.petBreedName}</span>
                        ) : null}
                        {selectedPet.petWeight ? (
                          <>
                            {selectedPet.petBreedName ? <span>·</span> : null}
                            <span>체중: {selectedPet.petWeight}kg</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 justify-center items-center ml-2 w-5 h-5 bg-orange-400 rounded-full">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500 bg-white rounded-xl border border-gray-200 shadow-sm">
                이용권에 연결된 반려동물 정보를 찾을 수 없습니다.
              </div>
            )}
          </div>

          {/* 날짜 선택 (반려동물 선택 시, 횟수만큼만 선택 가능) */}
          {selectedPet && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <h2 className="text-base font-semibold text-gray-800">
                    날짜 선택
                  </h2>
                  {!isHotel && maxSelectableDates > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      최대 {maxSelectableDates}일까지 선택 가능합니다.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="flex gap-1 items-center px-2 py-1 text-sm text-gray-600"
                  onClick={() => setIsDateListExpanded(!isDateListExpanded)}
                >
                  <motion.svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    animate={{ rotate: isDateListExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </motion.svg>
                </button>
              </div>

              {!isDateListExpanded && selectedDates.length > 0 && (
                <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex flex-wrap gap-2">
                    {selectedDates.map((date, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs font-medium text-orange-600 bg-orange-100 rounded"
                      >
                        {formatDate(date)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {isDateListExpanded && (
                <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <CalendarComponent
                    disabled={false}
                    onChange={handleDateChange}
                    selectedDays={selectedDates}
                    multiSelect={true}
                    disablePast={true}
                    maxDate={
                      fixedTicket?.expiredDate
                        ? new Date(fixedTicket.expiredDate)
                        : undefined
                    } // 발급 이용권 만료일 이후 날짜 선택 불가 (만료일 당일 포함)
                    disabledDates={disabledDates}
                    handleSelectDate={handleCalendarMonthChange}
                    schedules={manualReserveCalendarData ?? {}}
                  />
                </div>
              )}
            </div>
          )}

          {/* 시작 시간 선택 (호텔 이용권 전용) */}
          {selectedPet && isHotel && (
            <div className="mt-6">
              <div className="mb-2">
                <h2 className="text-base font-semibold text-gray-800">
                  시작 시간
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  호텔 체크인 시작 시간을 선택해주세요.
                </p>
              </div>

              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                {!firstSelectedDate ? (
                  <p className="text-sm text-center text-gray-400">
                    먼저 날짜를 선택해주세요.
                  </p>
                ) : hourOptions.length === 0 && isReservableTimeFetching ? (
                  <div className="space-y-3 animate-pulse">
                    <div className="grid grid-cols-6 gap-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div
                          key={`h-skel-${i}`}
                          className="h-9 bg-gray-100 rounded-lg"
                        />
                      ))}
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div
                          key={`m-skel-${i}`}
                          className="h-9 bg-gray-100 rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                ) : hourOptions.length === 0 ? (
                  <p className="text-sm text-center text-gray-400">
                    선택 가능한 시간이 없습니다.
                  </p>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs text-gray-500">
                        {formatDate(firstSelectedDate)} 기준
                      </span>
                      <span className="text-sm font-semibold text-orange-500">
                        {selectedHour && selectedMinute
                          ? `${selectedHour}:${selectedMinute}`
                          : "--:--"}
                      </span>
                    </div>

                    <div className="mb-2 text-xs font-medium text-gray-700">
                      시
                    </div>
                    <div className="grid grid-cols-6 gap-2 mb-4">
                      {hourOptions.map((h) => {
                        const active = selectedHour === h;
                        return (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setSelectedHour(h)}
                            className={
                              "py-2 text-sm font-medium rounded-lg border transition-colors " +
                              (active
                                ? "bg-orange-400 text-white border-orange-400"
                                : "bg-white text-gray-700 border-gray-200 hover:border-orange-300")
                            }
                          >
                            {h}
                          </button>
                        );
                      })}
                    </div>

                    <div className="mb-2 text-xs font-medium text-gray-700">
                      분
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {minuteOptions.map((m) => {
                        const active = selectedMinute === m;
                        return (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setSelectedMinute(m)}
                            className={
                              "py-2 text-sm font-medium rounded-lg border transition-colors " +
                              (active
                                ? "bg-orange-400 text-white border-orange-400"
                                : "bg-white text-gray-700 border-gray-200 hover:border-orange-300")
                            }
                          >
                            {m}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* 입소 시간 + 이용시간 선택 (데이케어 시간권 전용) */}
          {selectedPet && isDaycare && (
            <div className="mt-6">
              <div className="mb-2">
                <h2 className="text-base font-semibold text-gray-800">
                  입소 시간 / 이용시간
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  입소 시간(정시)과 이용시간을 선택해주세요. (1시간 = 이용권 1회)
                </p>
              </div>

              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                {!firstSelectedDate ? (
                  <p className="text-sm text-center text-gray-400">
                    먼저 날짜를 선택해주세요.
                  </p>
                ) : hourOptions.length === 0 && isReservableTimeFetching ? (
                  <div className="grid grid-cols-6 gap-2 animate-pulse">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={`dc-skel-${i}`}
                        className="h-9 bg-gray-100 rounded-lg"
                      />
                    ))}
                  </div>
                ) : hourOptions.length === 0 ? (
                  <p className="text-sm text-center text-gray-400">
                    선택 가능한 시간이 없습니다.
                  </p>
                ) : daycareUseHoursOptions.length === 0 ? (
                  <p className="text-sm text-center text-red-500">
                    잔여 이용권이 부족하여 예약할 수 없습니다.
                  </p>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs text-gray-500">
                        {formatDate(firstSelectedDate)} 기준
                      </span>
                      <span className="text-sm font-semibold text-orange-500">
                        {selectedHour && selectedUseHours
                          ? `${selectedHour}:00 ~ ${String(
                              Number.parseInt(selectedHour, 10) + selectedUseHours
                            ).padStart(2, "0")}:00`
                          : "--:--"}
                      </span>
                    </div>

                    <div className="mb-2 text-xs font-medium text-gray-700">
                      입소 시간
                    </div>
                    <div className="grid grid-cols-6 gap-2 mb-4">
                      {hourOptions.map((h) => {
                        const active = selectedHour === h;
                        return (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setSelectedHour(h)}
                            className={
                              "py-2 text-sm font-medium rounded-lg border transition-colors " +
                              (active
                                ? "bg-orange-400 text-white border-orange-400"
                                : "bg-white text-gray-700 border-gray-200 hover:border-orange-300")
                            }
                          >
                            {h}
                          </button>
                        );
                      })}
                    </div>

                    <div className="mb-2 text-xs font-medium text-gray-700">
                      이용시간
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {daycareUseHoursOptions.map((h) => {
                        const active = selectedUseHours === h;
                        return (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setSelectedUseHours(h)}
                            className={
                              "py-2 text-sm font-medium rounded-lg border transition-colors " +
                              (active
                                ? "bg-orange-400 text-white border-orange-400"
                                : "bg-white text-gray-700 border-gray-200 hover:border-orange-300")
                            }
                          >
                            {h}시간
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 하단 예약하기 버튼 */}
      {selectedPet && (
        <div className="fixed left-1/2 -translate-x-1/2 w-full max-w-[480px] bottom-0 md:bottom-6 md:rounded-b-3xl z-40 px-4 pt-3 pb-[calc(0.75rem+var(--nav-bottom,0px)+env(safe-area-inset-bottom,0px))] bg-white border-t border-gray-200 shadow-lg">
          <Button
            className="py-3 w-full text-base font-semibold text-white bg-orange-400 rounded-lg transition-colors hover:bg-orange-500 active:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleReservation}
            disabled={
              isReserving ||
              selectedDates.length === 0 ||
              (isHotel && (!selectedHour || !selectedMinute)) ||
              (isDaycare && (!selectedHour || !selectedUseHours))
            }
          >
            {isReserving ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                예약 처리 중...
              </span>
            ) : (
              "예약하기"
            )}
          </Button>
        </div>
      )}

      {/* [임시] 위탁계약 없어도 예약 가능하도록 주석 처리 - 계약 관련 다이얼로그 */}
      {/* <Dialog
        visible={showContractDialog}
        onClose={() => {
          setShowContractDialog(false);
          setSelectedPet(null);
          setIsPetListExpanded(true);
        }}
        persist={false}
      >
        <div className="flex flex-col gap-4">
          <div className="text-center">
            <h3 className="mb-2 text-lg font-semibold text-gray-800">
              위탁계약 필요
            </h3>
            <p className="text-sm text-gray-600">
              해당 반려견의 위탁계약이 필요합니다.
              <br />
              계약을 진행하시겠습니까?
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              className="flex-1 py-2 text-base text-gray-600 bg-white rounded-full border border-gray-300"
              onClick={() => {
                setShowContractDialog(false);
                setSelectedPet(null);
                setIsPetListExpanded(true);
              }}
            >
              취소
            </Button>
            <Button
              className="flex-1 py-2 text-base text-white bg-orange-400 rounded-full"
              onClick={() => {
                setShowContractDialog(false);
                setShowContractModal(true);
              }}
            >
              확인
            </Button>
          </div>
        </div>
      </Dialog> */}

      {/* 중복 예약 안내 다이얼로그 (같은 매장·같은 그룹) */}
      <Dialog
        visible={!!duplicateReserveMessage}
        onClose={() => setDuplicateReserveMessage(null)}
        persist={false}
      >
        <div className="p-6 text-center">
          <h3 className="mb-3 text-lg font-bold text-gray-800">
            이미 예약된 날짜입니다
          </h3>
          <p className="mb-6 text-sm text-gray-600 whitespace-pre-line">
            {duplicateReserveMessage}
          </p>
          <div className="flex justify-center">
            <Button
              className="px-8 py-3 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={() => setDuplicateReserveMessage(null)}
            >
              확인
            </Button>
          </div>
        </div>
      </Dialog>

      {/* 유치원 정원 초과 경고 (확인=진행 / 취소) */}
      <Dialog
        visible={!!capacityWarnDates}
        onClose={() => setCapacityWarnDates(null)}
        persist={false}
      >
        <div className="p-6 text-center">
          <p className="mb-6 text-sm text-gray-700 whitespace-pre-line">
            {capacityWarnDates
              ? `${capacityWarnDates
                  .map((d) => {
                    const [, m, day] = d.split("-");
                    return `${Number(m)}월 ${Number(day)}일`;
                  })
                  .join(", ")} 정원이 초과되었습니다.\n예약을 진행하시겠습니까?`
              : ""}
          </p>
          <div className="flex gap-2">
            <button
              className="flex-1 py-3 font-semibold text-gray-700 bg-gray-200 rounded-lg"
              onClick={() => setCapacityWarnDates(null)}
            >
              취소
            </button>
            <button
              className="flex-1 py-3 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={() => {
                capacityConfirmedRef.current = true;
                setCapacityWarnDates(null);
                handleReservation();
              }}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 예약 완료 다이얼로그 */}
      <Dialog
        visible={showReservationCompleteDialog}
        onClose={() => {
          setShowReservationCompleteDialog(false);
          handleBackClick();
        }}
        persist={false}
      >
        <div className="p-6 text-center">
          <h3 className="mb-4 text-lg font-bold">예약 신청 완료</h3>
          {selectedDates.length > 0 && (
            <div className="p-3 mb-4 bg-gray-100 rounded-lg">
              <span className="font-bold text-black">
                {selectedDates.length === 1
                  ? formatDate(selectedDates[0]).replace(/-/g, ".")
                  : `${formatDate(selectedDates[0]).replace(/-/g, ".")} ~ ${formatDate(selectedDates[selectedDates.length - 1]).replace(/-/g, ".")}`}
              </span>
            </div>
          )}
          <p className="mb-6 text-sm text-gray-600">
            {getReservationCompleteMessage(cdProductType)}
          </p>
          <div className="flex justify-center">
            <Button
              className="px-8 py-3 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={() => {
                setShowReservationCompleteDialog(false);
                handleBackClick();
              }}
            >
              확인
            </Button>
          </div>
        </div>
      </Dialog>

      {/* 수기예약: 활성 사업장 + 미계약 → 예약은 접수됐고 계약서 발송 안내(선택) */}
      <Dialog
        visible={showContractNotice}
        onClose={() => {
          setShowContractNotice(false);
          handleBackClick();
        }}
        persist={false}
      >
        <div className="p-6 text-center">
          <h3 className="mb-3 text-lg font-bold text-gray-800">예약 접수 완료</h3>
          <p className="mb-2 text-sm text-gray-600">
            예약이 접수되었어요.
          </p>
          <p className="mb-6 text-sm text-gray-600">
            다만 이 고객은 아직{" "}
            <strong className="text-orange-500">계약이 완료되지 않았어요.</strong>
            <br />
            계약서를 발송해 서명을 받으시겠어요?
          </p>
          <div className="flex gap-3">
            <Button
              className="flex-1 py-3 text-base font-semibold text-gray-600 bg-white rounded-lg border border-gray-300"
              onClick={() => {
                setShowContractNotice(false);
                handleBackClick();
              }}
            >
              발송 없이 진행
            </Button>
            <Button
              className="flex-1 py-3 text-base font-semibold text-white bg-orange-400 rounded-lg"
              onClick={() => {
                setShowContractNotice(false);
                navigate({ to: "/business/layout/contract-request-send" });
              }}
            >
              계약서 발송
            </Button>
          </div>
        </div>
      </Dialog>

      {/* [임시] 위탁계약 없어도 예약 가능하도록 주석 처리 - 계약 모달 (business: GET /business/manual/reserve/contract) */}
      {/* <ContractModal
        visible={showContractModal}
        onClose={() => {
          setShowContractModal(false);
          setSelectedPet(null);
          setIsPetListExpanded(true);
        }}
        onConfirm={() => {
          setShowContractModal(false);
          refetchPetList();
        }}
        selectedPet={
          selectedPet
            ? {
                petName: selectedPet.petName,
                fileUrl: selectedPet.fileUrl ?? null,
                petSeq: selectedPet.petSeq,
              }
            : undefined
        }
        companySeq={companySeq}
        userSeq={state?.userSeq}
        contractApiMode="business"
      /> */}
    </>
  );
}
